import React, { useMemo, useState } from "react";
import { useUserAndGameContext } from "../../../../contexts/UserAndGameContext";
import { useSuperPickemContext } from "../../../SuperPickem/context/SuperPickemContext";
import { Typography, Box } from "@mui/material";
import { DateTime } from "luxon";
import { hexToRgba } from "../../../Brackets/bracketUtils/HexToRgba";
import SuperPickemSingleEntryView from "./SuperPickemSingleEntryView";

const SuperPickemUserDash = ({ canIAddAnEntry, setIndexToRemove, setRemoveModalOpen }) => {
    const {
        allUsersEntries,
        usersIndexesInThisPool,
        myLeaderboardInfo,
        masterPoolData,
        pickIndex,
        hasJoinDeadlinePassed,
        setJoinOpen,
        isThisPoolPartyGame,
        isUserAdmin,    
        league,
        pickState,
    } = useUserAndGameContext();

    const { 
        teams,
        maxPossiblePoints,
        todaysInterval
     } = useSuperPickemContext();
     //console.log("Todays Interval: ", todaysInterval, "Pick State: ", pickState);

     const [imageLoading, setImageLoading] = useState(true);
    const [imageError, setImageError] = useState(false);

    const firstGameTime = masterPoolData?.gameParameters?.firstGameTime;
    const firstGameTimeInNewYork = firstGameTime ? DateTime.fromISO(firstGameTime, { zone: 'America/New_York' }) : null;
    const nowInNewYork = DateTime.now().setZone('America/New_York');
    const hasFirstGameStarted = firstGameTimeInNewYork ? firstGameTimeInNewYork < nowInNewYork : false;

    const picksToMake = masterPoolData?.gameParameters?.picksToMake;
    const usingSeriesLengths = masterPoolData?.gameParameters?.usingSeriesLengths;
    const rounds = masterPoolData?.gameParameters?.rounds;
    const lastRound = rounds ? rounds[rounds.length - 1] : null;

    const isTodaysPickIn = useMemo(() => {
        if (!pickState || !todaysInterval) return false;
        const todaysPick = pickState?.find(pick => pick?.interval === todaysInterval);
        return todaysPick ? true : false;
    }, [pickState, todaysInterval]);
    //console.log("Is Todays Pick In: ", isTodaysPickIn);

    function haveAllPicksBeenMade(pool, picksToMake, usingSeriesLengths) {
        if (!pool) return false;
        
        const picks = pool?.bracketPicks;
        const seriesLengths = pool?.seriesLengths;
        //console.log('picks', picks, "SeriesLengths", seriesLengths, "picksToMake", picksToMake);

        if (!picks || picks?.length !== picksToMake) {
            return false;
        }

        if (usingSeriesLengths && (!seriesLengths || seriesLengths.length !== picksToMake)) {
            return false;
        }

        return true;
    }

    function findChampId(picks) {
        if (!picks || !lastRound) return null;
        const champPick = picks?.find(pick => pick.round === lastRound);
        return champPick?.teamId;
    }

    function findChampResult(picks) {
        if (!picks || !lastRound) return null;
        
        const champPick = picks?.find(pick => pick.round === lastRound);
        return champPick?.result;
    }

    function findMyRank(myLeaderboardInfo, entryName, rankType) {
        if (!myLeaderboardInfo || !entryName) return null;
        
        const entry = myLeaderboardInfo?.myInfo?.find(entry => entry?.username === entryName);
        if (!entry) return '-';

        let rank;
        if (rankType === "overall") {
            rank = entry?.overallRank;
        } else if (rankType === "bracket") {
            rank = entry?.bracketRank;
        } else if (rankType === "daily") {
            rank = entry?.dailyPicksRank;
        } else {
            return '-';
        }

        return rank ? rank : '-';
    }

    function findMyTotalPoints(myLeaderboardInfo, entryName) {
        if (!myLeaderboardInfo || !entryName) return null;

        const points = myLeaderboardInfo?.myInfo?.find(entry => entry?.username === entryName)?.totalPoints;
        return points ? points : 0;
    }

    function findMyBracketPoints(myLeaderboardInfo, entryName) {
        if (!myLeaderboardInfo || !entryName) return null;
        
        const points = myLeaderboardInfo?.myInfo?.find(entry => entry?.username === entryName)?.bracketPoints;
        return points ? points : 0;
    }

    function findMyDailyPicksPoints(myLeaderboardInfo, entryName) {
        if (!myLeaderboardInfo || !entryName) return null;
        
        const points = myLeaderboardInfo?.myInfo?.find(entry => entry?.username === entryName)?.dailyPicksPoints;
        return points ? points : 0;
    }

    function findMyMaxPossiblePoints(myLeaderboardInfo, entryName) {
        if (!myLeaderboardInfo || !entryName) return null;
        
        const maxPoints = myLeaderboardInfo?.myInfo?.find(entry => entry?.username === entryName)?.maxPossiblePoints;
        return maxPoints ? maxPoints : 0;
    }

    const totalEntriesCount = myLeaderboardInfo?.entryCount || 0;

    const handleImageLoad = () => {
        setImageLoading(false);
        setImageError(false);
    };

    const handleImageError = () => {
        setImageLoading(false);
        setImageError(true);
    };

    const getChampBoxStyle = (result) => {
        if (result === 'win') {
            return {
                border: '2px solid #00AA72',
                backgroundColor: hexToRgba('#00AA72', 0.2)
            };
        } else if (result === 'loss') {
            return {
                border: '2px solid #CC293C',
                backgroundColor: hexToRgba('#CC293C', 0.2)
            };
        }
        return {
            border: '1px solid #002129',
            backgroundColor: 'transparent'
        };
    };

       // Get entries for this pool
       const entriesInThisPool = usersIndexesInThisPool
       .filter(index => index >= 0 && index < allUsersEntries.length)
       .map(index => allUsersEntries[index]);

       return (
        <Box>
            {entriesInThisPool?.length === 1 && !canIAddAnEntry && (
                <SuperPickemSingleEntryView
                    entry={entriesInThisPool[0]}
                    totalEntriesCount={totalEntriesCount}
                    hasFirstGameStarted={hasFirstGameStarted}
                    picksToMake={picksToMake}
                    league={league}
                    teams={teams}
                    haveAllPicksBeenMade={haveAllPicksBeenMade}
                    findChampId={findChampId}
                    findChampResult={findChampResult}
                    handleImageError={handleImageError}
                    handleImageLoad={handleImageLoad}
                    getChampBoxStyle={getChampBoxStyle}
                    imageError={imageError}
                    imageLoading={imageLoading}
                    findMyRank={findMyRank}
                    findMyTotalPoints={findMyTotalPoints}
                    findMyBracketPoints={findMyBracketPoints}
                    findMyDailyPicksPoints={findMyDailyPicksPoints}
                    findMyMaxPossiblePoints={findMyMaxPossiblePoints}
                    myLeaderboardInfo={myLeaderboardInfo}
                    hasJoinDeadlinePassed={hasJoinDeadlinePassed}
                    setIndexToRemove={setIndexToRemove}
                    setRemoveModalOpen={setRemoveModalOpen}
                    isThisPoolPartyGame={isThisPoolPartyGame}
                    pickIndex={pickIndex}
                    isUserAdmin={isUserAdmin}
                    isTodaysPickIn={isTodaysPickIn}
                />
            )}
        </Box>
       );
    }

export default SuperPickemUserDash;